import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { clearCart } from "../store/slices/cartSlice";
import { createOrder } from "../store/slices/orderSlice";
import { RootState, AppDispatch } from "../store";
import { resetCheckout } from "../store/slices/checkoutSlice";

const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [error, setError] = useState<string | null>(null);
  const { items, totalPrice } = useSelector((state: RootState) => state.cart);
  const { shippingAddress } = useSelector((state: RootState) => state.checkout);

  useEffect(() => {
    const createOrderFromPayment = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const paymentIntentId = searchParams.get("payment_intent");
        const paymentStatus = searchParams.get("redirect_status");

        if (!paymentIntentId || paymentStatus !== "succeeded") {
          setError("Payment verification failed");
          return;
        }

        if (!shippingAddress) {
          setError("Shipping address not found");
          return;
        }

        await dispatch(
          createOrder({
            items: items.map((item) => ({
              productId: item.id,
              quantity: item.quantity,
              price: item.price,
            })),
            totalAmount: totalPrice,
            paymentIntentId,
            status: "completed",
            shippingAddress,
          }),
        ).unwrap();

        dispatch(clearCart());
        dispatch(resetCheckout());

        setTimeout(() => {
          navigate("/orders");
        }, 5000);
      } catch (err) {
        console.error("Failed to create order:", err);
        setError("Failed to create your order. Please contact support.");
      }
    };

    createOrderFromPayment();
  }, [dispatch, items, totalPrice, navigate, shippingAddress]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090F]">
        <div className="max-w-md w-full bg-[#13131C] border border-[#252535] rounded-lg p-8 text-center">
          <div className="mb-6">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2
            data-test="payment-error"
            className="text-2xl font-bold text-[#F0EEFF] mb-4"
          >
            Payment Error
          </h2>
          <p data-test="error-message" className="text-[#9B9BAD] mb-8">
            {error}
          </p>
          <button
            data-test="return-to-cart"
            onClick={() => navigate("/cart")}
            className="bg-[#FF4500] text-white px-6 py-2 rounded hover:bg-[#FF6B47]"
          >
            Return to Cart
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090F]">
      <div className="max-w-md w-full bg-[#13131C] border border-[#252535] rounded-lg p-8 text-center">
        <div className="mb-6">
          <svg
            className="mx-auto h-12 w-12 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2
          data-test="payment-success"
          className="text-2xl font-bold text-[#F0EEFF] mb-4"
        >
          Payment Successful!
        </h2>
        <p className="text-[#9B9BAD] mb-8">
          Thank you for your purchase. We're processing your order and will send
          you a confirmation email shortly.
        </p>
        <div className="text-sm text-[#7A7A8C]">
          You will be redirected to your orders in a few seconds...
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
